/** Phase A notification center copy. Keep parity across all seven locales. */
type Copy = {
  title: string; all: string; actions: string; updates: string;
  empty: string; emptyFiltered: string; viewAll: string;
  unreadCount: string; unreadCount_one?: string; unreadCount_other?: string; markRead: string; dismiss: string;
  inviteTitle: string; inviteBody: string; reportTitle: string; reportBody: string;
  clubTitle: string; clubBody: string; rankingTitle: string; rankingBody: string;
  reviewTitle: string; reviewBody: string; open: string; review: string;
  actionRequired: string; suggestion: string;
  profileTitle: string; profileBody: string; duplicatesTitle: string; duplicatesBody: string;
  clubsReviewTitle: string; clubsReviewBody: string; clubsDuplicateTitle: string; clubsDuplicateBody: string;
  duplicatesIntro: string; duplicatesDisclaimer: string;
};
export const NOTIFICATION_CENTER_OVERLAY: Record<string, { notificationCenter: Copy }> = {
  en: { notificationCenter: {
    title: "Notifications", all: "All", actions: "To do", updates: "Updates",
    empty: "You're all caught up.", emptyFiltered: "No notifications here.",
    viewAll: "See all notifications", unreadCount: "{{count}} unread",
    markRead: "Mark as read", dismiss: "Don't suggest again",
    inviteTitle: "New match invitation", inviteBody: "{{name}} wants to connect a match from {{date}}.",
    reportTitle: "Performance Report ready", reportBody: "Your shared report with {{name}} is ready.",
    clubTitle: "Choose your primary club", clubBody: "Add your main tennis club to complete your playing profile.",
    rankingTitle: "Appear in the Elo leaderboard", rankingBody: "Your ranking is private. You can choose to appear in public leaderboards.",
    reviewTitle: "Enjoying Tennis Log?", reviewBody: "You've logged at least five matches. A Google Play review would help us grow.",
    profileTitle: "Complete your player profile",
    profileBody: "Some important player details are missing. Review them in Settings.",
    duplicatesTitle: "Possible duplicate opponent names",
    duplicatesBody: "Saved opponent names look similar. Review whether they describe the same person.",
    clubsReviewTitle: "Review historical club names",
    clubsReviewBody: "Some past matches may be ready for a verified club association.",
    clubsDuplicateTitle: "Possible duplicate clubs",
    clubsDuplicateBody: "Some clubs you selected have very similar names in the same city. Check your memberships before changing them.",
    duplicatesIntro: "Possible duplicate profiles",
    duplicatesDisclaimer: "Similar names do not prove identity. No records are automatically merged or changed.",
    open: "Review", review: "Open Google Play", actionRequired: "Needs attention", suggestion: "Suggestion",
  }},
  el: { notificationCenter: {
    title: "Ειδοποιήσεις", all: "Όλες", actions: "Εκκρεμότητες", updates: "Ενημερώσεις",
    empty: "Δεν έχεις εκκρεμότητες.", emptyFiltered: "Δεν υπάρχουν ειδοποιήσεις εδώ.",
    viewAll: "Όλες οι ειδοποιήσεις", unreadCount: "{{count}} μη αναγνωσμένες", unreadCount_one: "{{count}} μη αναγνωσμένη", unreadCount_other: "{{count}} μη αναγνωσμένες",
    markRead: "Σήμανση ως αναγνωσμένη", dismiss: "Να μην προταθεί ξανά",
    inviteTitle: "Νέο αίτημα σύνδεσης αγώνα", inviteBody: "Ο/Η {{name}} θέλει να συνδέσετε αγώνα της {{date}}.",
    reportTitle: "Έτοιμο Performance Report", reportBody: "Το κοινό report με τον/την {{name}} είναι διαθέσιμο.",
    clubTitle: "Επίλεξε το κύριο κλαμπ σου", clubBody: "Συμπλήρωσε το βασικό tennis club στο προφίλ σου.",
    rankingTitle: "Εμφανίσου στην κατάταξη Elo", rankingBody: "Η κατάταξή σου είναι ιδιωτική. Μπορείς να ενεργοποιήσεις τη δημόσια εμφάνισή σου.",
    reviewTitle: "Σου αρέσει το Tennis Log;", reviewBody: "Έχεις καταχωρήσει τουλάχιστον πέντε αγώνες. Μια αξιολόγηση στο Google Play μάς βοηθά.",
    profileTitle: "Ολοκλήρωσε το προφίλ παίκτη",
    profileBody: "Λείπουν βασικά στοιχεία παίκτη. Έλεγξέ τα στις Ρυθμίσεις.",
    duplicatesTitle: "Πιθανές διπλοεγγραφές αντιπάλων",
    duplicatesBody: "Υπάρχουν παρόμοια ονόματα αντιπάλων. Έλεγξε αν πρόκειται για το ίδιο άτομο.",
    clubsReviewTitle: "Έλεγξε ομίλους παλιών αγώνων",
    clubsReviewBody: "Κάποιοι παλιοί αγώνες ίσως αντιστοιχούν σε επαληθευμένο όμιλο.",
    clubsDuplicateTitle: "Πιθανές διπλές εγγραφές ομίλων",
    clubsDuplicateBody: "Ορισμένοι όμιλοι που έχεις επιλέξει έχουν παρόμοιο όνομα στην ίδια πόλη. Έλεγξε πρώτα τις συμμετοχές σου.",
    duplicatesIntro: "Πιθανές διπλοεγγραφές για έλεγχο",
    duplicatesDisclaimer: "Παρόμοια ονόματα δεν σημαίνουν απαραίτητα ίδιο άτομο. Δεν γίνεται αυτόματη συγχώνευση ή αλλαγή.",
    open: "Έλεγχος", review: "Google Play", actionRequired: "Απαιτείται ενέργεια", suggestion: "Πρόταση",
  }},
  es: { notificationCenter: {
    title: "Notificaciones", all: "Todas", actions: "Pendientes", updates: "Novedades",
    empty: "Estás al día.", emptyFiltered: "No hay notificaciones aquí.",
    viewAll: "Ver todas las notificaciones", unreadCount: "{{count}} sin leer",
    markRead: "Marcar como leída", dismiss: "No volver a sugerir",
    inviteTitle: "Nueva invitación a conectar un partido", inviteBody: "{{name}} quiere conectar un partido del {{date}}.",
    reportTitle: "Informe de rendimiento listo", reportBody: "Tu informe compartido con {{name}} está disponible.",
    clubTitle: "Elige tu club principal", clubBody: "Añade tu club de tenis principal al perfil.",
    rankingTitle: "Aparece en la clasificación Elo", rankingBody: "Tu clasificación es privada. Puedes hacerla visible.",
    reviewTitle: "¿Te gusta Tennis Log?", reviewBody: "Has registrado al menos cinco partidos. Una reseña en Google Play nos ayudaría.",
    profileTitle: "Completa tu perfil de jugador",
    profileBody: "Faltan datos importantes. Revísalos en Ajustes.",
    duplicatesTitle: "Posibles rivales duplicados",
    duplicatesBody: "Algunos nombres se parecen. Comprueba si corresponden a la misma persona.",
    clubsReviewTitle: "Revisa los clubes de partidos anteriores",
    clubsReviewBody: "Quizá puedas asociar algunos partidos antiguos con un club verificado.",
    clubsDuplicateTitle: "Posibles clubes duplicados",
    clubsDuplicateBody: "Algunos clubes elegidos tienen nombres muy similares en la misma ciudad. Revisa tus afiliaciones.",
    duplicatesIntro: "Posibles duplicados para revisar",
    duplicatesDisclaimer: "Un nombre parecido no demuestra identidad. No se fusionan ni modifican datos automáticamente.",
    open: "Revisar", review: "Google Play", actionRequired: "Requiere acción", suggestion: "Sugerencia",
  }},
  fr: { notificationCenter: {
    title: "Notifications", all: "Toutes", actions: "À faire", updates: "Actualités",
    empty: "Tout est à jour.", emptyFiltered: "Aucune notification ici.",
    viewAll: "Voir toutes les notifications", unreadCount: "{{count}} non lues", unreadCount_one: "{{count}} non lue", unreadCount_other: "{{count}} non lues",
    markRead: "Marquer comme lu", dismiss: "Ne plus proposer",
    inviteTitle: "Nouvelle invitation de match", inviteBody: "{{name}} souhaite connecter un match du {{date}}.",
    reportTitle: "Rapport de performance prêt", reportBody: "Votre rapport partagé avec {{name}} est disponible.",
    clubTitle: "Choisissez votre club principal", clubBody: "Complétez votre profil avec votre club de tennis principal.",
    rankingTitle: "Apparaître au classement Elo", rankingBody: "Votre classement est privé. Vous pouvez choisir de le rendre public.",
    reviewTitle: "Vous aimez Tennis Log ?", reviewBody: "Vous avez enregistré au moins cinq matchs. Un avis Google Play nous aiderait.",
    profileTitle: "Complétez votre profil de joueur",
    profileBody: "Il manque des informations importantes. Vérifiez-les dans les paramètres.",
    duplicatesTitle: "Adversaires potentiellement en double",
    duplicatesBody: "Des noms enregistrés se ressemblent. Vérifiez s’il s’agit de la même personne.",
    clubsReviewTitle: "Vérifiez les clubs des anciens matchs",
    clubsReviewBody: "Certains anciens matchs pourraient être associés à un club vérifié.",
    clubsDuplicateTitle: "Clubs potentiellement en double",
    clubsDuplicateBody: "Certains clubs sélectionnés portent des noms proches dans la même ville. Vérifiez vos affiliations.",
    duplicatesIntro: "Doublons possibles à vérifier",
    duplicatesDisclaimer: "Des noms proches ne prouvent pas qu’il s’agit de la même personne. Aucune fusion automatique.",
    open: "Consulter", review: "Google Play", actionRequired: "Action requise", suggestion: "Suggestion",
  }},
  de: { notificationCenter: {
    title: "Benachrichtigungen", all: "Alle", actions: "Offen", updates: "Neuigkeiten",
    empty: "Du bist auf dem Laufenden.", emptyFiltered: "Hier gibt es keine Benachrichtigungen.",
    viewAll: "Alle Benachrichtigungen", unreadCount: "{{count}} ungelesen",
    markRead: "Als gelesen markieren", dismiss: "Nicht erneut vorschlagen",
    inviteTitle: "Neue Spieleinladung", inviteBody: "{{name}} möchte ein Match vom {{date}} verknüpfen.",
    reportTitle: "Leistungsbericht bereit", reportBody: "Dein gemeinsamer Bericht mit {{name}} ist verfügbar.",
    clubTitle: "Hauptverein auswählen", clubBody: "Ergänze deinen Tennis-Hauptverein im Profil.",
    rankingTitle: "In der Elo-Rangliste erscheinen", rankingBody: "Deine Rangliste ist privat. Du kannst sie öffentlich machen.",
    reviewTitle: "Gefällt dir Tennis Log?", reviewBody: "Du hast mindestens fünf Matches erfasst. Eine Google-Play-Bewertung hilft uns.",
    profileTitle: "Spielerprofil vervollständigen",
    profileBody: "Wichtige Angaben fehlen noch. Prüfe sie in den Einstellungen.",
    duplicatesTitle: "Mögliche doppelte Gegnerprofile",
    duplicatesBody: "Einige Namen ähneln sich. Prüfe, ob sie dieselbe Person meinen.",
    clubsReviewTitle: "Vereine älterer Matches prüfen",
    clubsReviewBody: "Manche ältere Matches könnten einem bestätigten Verein zugeordnet werden.",
    clubsDuplicateTitle: "Mögliche doppelte Vereine",
    clubsDuplicateBody: "Einige deiner ausgewählten Vereine haben ähnliche Namen in derselben Stadt. Prüfe deine Mitgliedschaften.",
    duplicatesIntro: "Mögliche Duplikate prüfen",
    duplicatesDisclaimer: "Ähnliche Namen beweisen keine Identität. Daten werden nicht automatisch zusammengeführt.",
    open: "Prüfen", review: "Google Play", actionRequired: "Handlung nötig", suggestion: "Vorschlag",
  }},
  it: { notificationCenter: {
    title: "Notifiche", all: "Tutte", actions: "Da fare", updates: "Aggiornamenti",
    empty: "Non hai attività in sospeso.", emptyFiltered: "Nessuna notifica qui.",
    viewAll: "Vedi tutte le notifiche", unreadCount: "{{count}} non lette", unreadCount_one: "{{count}} non letta", unreadCount_other: "{{count}} non lette",
    markRead: "Segna come letta", dismiss: "Non suggerire più",
    inviteTitle: "Nuovo invito a collegare un match", inviteBody: "{{name}} vuole collegare un match del {{date}}.",
    reportTitle: "Report prestazioni pronto", reportBody: "Il report condiviso con {{name}} è disponibile.",
    clubTitle: "Scegli il tuo club principale", clubBody: "Completa il profilo con il tuo tennis club principale.",
    rankingTitle: "Compari nella classifica Elo", rankingBody: "La tua classifica è privata. Puoi renderla pubblica.",
    reviewTitle: "Ti piace Tennis Log?", reviewBody: "Hai registrato almeno cinque match. Una recensione su Google Play ci aiuterebbe.",
    profileTitle: "Completa il profilo giocatore",
    profileBody: "Mancano alcuni dati importanti. Controllali nelle Impostazioni.",
    duplicatesTitle: "Possibili avversari duplicati",
    duplicatesBody: "Alcuni nomi sono simili. Verifica se indicano la stessa persona.",
    clubsReviewTitle: "Controlla i club delle partite passate",
    clubsReviewBody: "Alcune partite potrebbero essere collegate a un club verificato.",
    clubsDuplicateTitle: "Possibili club duplicati",
    clubsDuplicateBody: "Alcuni club selezionati hanno nomi simili nella stessa città. Controlla le tue associazioni.",
    duplicatesIntro: "Possibili duplicati da verificare",
    duplicatesDisclaimer: "Nomi simili non provano l’identità. I dati non vengono uniti o modificati automaticamente.",
    open: "Controlla", review: "Google Play", actionRequired: "Azione richiesta", suggestion: "Suggerimento",
  }},
  ja: { notificationCenter: {
    title: "通知", all: "すべて", actions: "対応が必要", updates: "更新",
    empty: "新しい通知はありません。", emptyFiltered: "この項目に通知はありません。",
    viewAll: "すべての通知を見る", unreadCount: "未読 {{count}} 件",
    markRead: "既読にする", dismiss: "今後表示しない",
    inviteTitle: "試合の連携リクエスト", inviteBody: "{{name}} さんが {{date}} の試合を連携しようとしています。",
    reportTitle: "パフォーマンスレポートが完成", reportBody: "{{name}} さんとの共有レポートを確認できます。",
    clubTitle: "メインクラブを登録", clubBody: "テニスクラブをプロフィールに追加しましょう。",
    rankingTitle: "Elo ランキングに表示", rankingBody: "現在ランキングは非公開です。公開を選べます。",
    reviewTitle: "Tennis Log はいかがですか？", reviewBody: "5 試合以上を記録しました。Google Play のレビューをお願いします。",
    profileTitle: "選手プロフィールを完成させる",
    profileBody: "基本情報に未入力の項目があります。設定から確認してください。",
    duplicatesTitle: "重複している可能性のある対戦相手",
    duplicatesBody: "保存された名前が似ています。同じ人物か確認してください。",
    clubsReviewTitle: "過去の試合のクラブ名を確認",
    clubsReviewBody: "過去の試合を確認済みクラブに関連付けられる可能性があります。",
    clubsDuplicateTitle: "重複している可能性のあるクラブ",
    clubsDuplicateBody: "同じ都市で、選択したクラブ名が似ています。登録内容を確認してください。",
    duplicatesIntro: "確認が必要な候補",
    duplicatesDisclaimer: "名前が似ていても同一人物とは限りません。データは自動的に統合・変更されません。",
    open: "確認", review: "Google Play", actionRequired: "対応が必要", suggestion: "おすすめ",
  }},
};
